import React, { useState, useRef, useCallback, useEffect, Suspense, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { gameTypes } from '../data/GameTypes';
import { motion, AnimatePresence } from 'framer-motion';
import { PulseLoader } from 'react-spinners';
import { SparklesIcon, RocketLaunchIcon, ChevronDownIcon, StarIcon } from '@heroicons/react/24/outline';
import { PlayIcon } from '@heroicons/react/24/solid';
import { CheckIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { Disclosure } from '@headlessui/react';
import { getStylePresets } from '../campaign/stylePresets';
import FormSwitch from './FormSwitch';
import QuickStyleSelector from '../campaign/QuickStyleSelector';


export const getGameTypeColor = (gameType) => {
  const colorMap = {
    "pindrop": "#EF4444", // red-500
    "hint": "#F59E0B",    // amber-500
    "quiz": "#3B82F6",    // blue-500
    "mapselect": "#10B981", // emerald-500
    "snake": "#8B5CF6"    // purple-500
  };
  return colorMap[gameType] || "#4F46E5"; // default to indigo-600
};

const getGridCols = (numOptions) => {
  if (numOptions <= 2) return 'grid-cols-1';
  if (numOptions <= 3) return 'grid-cols-1 md:grid-cols-3';
  if (numOptions <= 4) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
  return 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4';
};

// Replace the dynamic function with a static mapping:
const setupComponents = {
  cubefield: React.lazy(() => import('../games/cubefield/CubeFieldPublicAppearanceSection')),
  snake: React.lazy(() => import('../games/snake/SnakePublicAppearanceSection')),
  pindrop: React.lazy(() => import('../games/pindrop/PindropPublicAppearanceSection')),
  mapselect: React.lazy(() => import('../games/mapselect/MapPublicAppearanceSection')),
};



const PublicGameCreator = ({ config = {}, user: propUser, businessProfile: propBusinessProfile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Default configuration for public games
  const defaultConfig = {
    isStudioMode: false,
    showPrivacyToggle: true,
    defaultPrivate: true,
    headerText: {
      title: "Create Your Game",
      subtitle: "Build and share epic games with friends"
    },
    stepTitles: {
      step1: "Choose your game type",
      step2: "Set up your game",
      step3: "Almost ready!",
      step4: "🎉 Game Created!"
    },
    buttonText: {
      create: "🚀 Create Game",
      success: "🎉 Game Created!"
    },
    redirectPath: null, // No redirect for public games
    collectionName: "campaign",
    additionalData: {}
  };
  
  // Merge provided config with defaults
  const finalConfig = {
    ...defaultConfig,
    ...config,
    headerText: { ...defaultConfig.headerText, ...config.headerText },
    stepTitles: { ...defaultConfig.stepTitles, ...config.stepTitles },
    buttonText: { ...defaultConfig.buttonText, ...config.buttonText },
    additionalData: { ...defaultConfig.additionalData, ...config.additionalData }
  };
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [isRegenerating, setIsRegenerating] = useState({});
  const [useAI, setUseAI] = useState(true);
  const [selectedStyle, setSelectedStyle] = useState('retro'); // Use 'retro' as default for public games
  const [isBackward, setIsBackward] = useState(false);
  const [fontLoaded, setFontLoaded] = useState(false);
  
  const [formData, setFormData] = useState({
    gameType: null,
    shortDescription: '',
    description: '',
    groupName: '',
    aiGeneratedContent: null
  });

  // Validation constants
  const MAX_GROUP_NAME_LENGTH = 20;

  // Add state for regeneration popup
  const [regenPopup, setRegenPopup] = useState({ open: false, elementType: null, currentData: null, index: null });
  const [regenComment, setRegenComment] = useState('');

  // Add state for manual title/intro for non-AI/no-setup
  const [manualTitle, setManualTitle] = useState('');
  const [manualIntro, setManualIntro] = useState('');

  // Add state for CubeFieldAppearanceSection and PlayerIconSection
  const [publicGameOptions, setPublicGameOptions] = useState({
    theme: { colors: { game: {} }, typography: {} }
  });
  const [activeColorPicker, setActiveColorPicker] = useState(null);

  // Add state for private option
  const [isPrivate, setIsPrivate] = useState(finalConfig.defaultPrivate);
  const [rateLimitError, setRateLimitError] = useState(null);

  // Add rate limiting state
  const [requestCount, setRequestCount] = useState(0);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const MAX_REQUESTS_PER_HOUR = 10;
  const HOUR_IN_MS = 60 * 60 * 1000;

  const [copySuccess, setCopySuccess] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // Store the combined style presets from QuickStyleSelector
  const [allStylePresets, setAllStylePresets] = useState({});

  // Ref for the main game creation container
  const gameCreationRef = useRef(null);

  // Scroll to game creation container when step changes
  useEffect(() => {
    if (gameCreationRef.current) {
      const rect = gameCreationRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const targetPosition = rect.top + scrollTop - 20; // 20px above the container
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  }, [step]);

  // Handle pre-selected game type from landing page
  useEffect(() => {
    if (location.state?.selectedGameType && !formData.gameType) {
      setFormData(prev => ({
        ...prev,
        gameType: location.state.selectedGameType
      }));
      setStep(2);
    }
  }, [location.state, formData.gameType]);

  useEffect(() => {
    // Load Share Tech Mono font (retro style) and apply global styles
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Share+Tech+Mono:wght@400&display=swap';
    link.rel = 'stylesheet';
    link.rel = 'preload';
    link.as = 'style';
    link.onload = function() { this.rel = 'stylesheet'; };
    document.head.appendChild(link);
    
    // Add global styles to prevent jankiness
    const style = document.createElement('style');
    style.textContent = `
      body {
        font-family: "Share Tech Mono", monospace;
        transition: all 0.3s ease-in-out;
      }
    `;
    document.head.appendChild(style);
    
    setFontLoaded(true); // Set immediately to prevent flash
    
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const retroStyle = {
    fontFamily: '"Share Tech Mono", monospace',
    transition: 'all 0.3s ease-in-out'
  };

  const checkRateLimit = () => {
    const now = Date.now();
    const timeWindow = HOUR_IN_MS; // 1 hour
    const requestsInWindow = requestCount;
    
    if (now - lastRequestTime > timeWindow) {
      // Reset if window has passed
      setRequestCount(1);
      setLastRequestTime(now);
      return true;
    }
    
    if (requestsInWindow >= MAX_REQUESTS_PER_HOUR) {
      const timeLeft = Math.ceil((timeWindow - (now - lastRequestTime)) / (60 * 1000));
      setRateLimitError(`🎮 Whoa there, game creator! You've made ${MAX_REQUESTS_PER_HOUR} games in the last hour. Please wait ${timeLeft} minutes before creating another one. This helps us keep the service fast for everyone!`);
      return false;
    }
    
    setRequestCount(prev => prev + 1);
    return true;
  };

  const loadingMessages = [
    "🎮 Booting up the game engine...",
    "⚡ Generating epic content...",
    "🎲 Rolling for awesome ideas...",
    "🎯 Calibrating fun levels...",
    "🚀 Final preparations...",
    "✨ Adding that special magic..."
  ];

  useEffect(() => {
    if (isGenerating) {
      let currentIndex = 0;
      const interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % loadingMessages.length;
        setLoadingMessage(loadingMessages[currentIndex]);
      }, 4000);

      return () => clearInterval(interval);
    }
  }, [isGenerating]);

  useEffect(() => {
    if (
      step === 2 &&
      formData.gameType &&
      !formData.gameType.requiresAIGeneration
    ) {
      // Set manual fields from formData if available, otherwise use defaults from game type
      setManualTitle(formData.shortDescription || formData.gameType.options.title || '');
      setManualIntro(formData.description || formData.gameType.options.intro || '');
    }
  }, [step, formData.gameType]);



  const handleGameTypeSelect = (gameType) => {
    setFormData(prev => ({
      gameType,
      shortDescription: '',
      description: '',
      groupName: '',
      aiGeneratedContent: null
    }));
    // Clear manual fields when switching game types
    setManualTitle('');
    setManualIntro('');
    setStep(2);
  };

  const handleBasicInfoSubmit = async (e) => {
    e.preventDefault();
    
    if (!checkRateLimit()) {
      return;
    }
    
    if (formData.gameType?.requiresAIGeneration) {
      setIsGenerating(true);
      setLoadingMessage(loadingMessages[0]);
      
      // Scroll to top of game creation container
      if (gameCreationRef.current) {
        const rect = gameCreationRef.current.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = rect.top + scrollTop - 20; // 20px above the container
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
      
      try {
        if (!window.grecaptcha) {
          setRateLimitError('🤖 Security check failed to load. Please refresh the page and try again. If the problem continues, try disabling your ad blocker.');
          return;
        }

        if (!process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
          setRateLimitError('🤖 reCAPTCHA configuration error. Please contact support.');
          return;
        }

        // Get reCAPTCHA v3 token
        const token = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {action: 'create_game'});
        
        if (!token) {
          setRateLimitError('🤖 Security verification failed. Please refresh the page and try again. Make sure JavaScript is enabled in your browser.');
          return;
        }

        if (token.length < 20) {
          setRateLimitError('🤖 Invalid security token generated. Please try again.');
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/app/generate-game`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            gameTypeId: formData.gameType.id,
            shortDescription: formData.shortDescription
          }),
        });

        if (response.status === 429) {
          setRateLimitError('⏰ You\'re creating games too quickly! Please wait a few minutes before trying again to help us keep the service running smoothly.');
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to generate game content');
        }

        const responseData = await response.json();
        
        setFormData(prev => ({
          ...prev,
          aiGeneratedContent: {
            options: responseData.message.options
          }
        }));
        
        setStep(3);
      } catch (error) {
        
        // Provide specific error messages based on error type
        if (error.message?.includes('reCAPTCHA') || error.message?.includes('Invalid reCAPTCHA')) {
          setRateLimitError('🤖 Security check failed. Please refresh the page and try again. If this keeps happening, try using a different browser or disabling ad blockers.');
        } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
          setRateLimitError('🌐 Connection problem! Please check your internet connection and try again.');
        } else if (error.message?.includes('timeout')) {
          setRateLimitError('⏱️ The request took too long. Please try again!');
        } else if (error.message?.includes('500') || error.message?.includes('server')) {
          setRateLimitError('🔧 Our servers are having a moment. Please try again in a few seconds!');
        } else {
          setRateLimitError(`❌ Couldn't create your game: ${error.message || 'Please try again or contact support if this keeps happening.'}`);
        }
      } finally {
        setIsGenerating(false);
      }
    } else {
      setStep(3);
    }
  };

  const handleRegenerate = useCallback(async (elementType, currentData, index, comment) => {
    if (!formData.gameType?.requiresAIGeneration) return;

    const regenerationKey = index !== undefined ? `${elementType}_${index}` : elementType;
    setIsRegenerating(prev => ({ ...prev, [regenerationKey]: true }));

    try {
      // Get reCAPTCHA token before making the request
      let token = null;
      if (window.grecaptcha) {
        token = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {
          action: 'regenerate_content'
        });
      }

      const requestBody = {
        elementType,
        currentData,
        gameTypeId: formData.gameType?.id,
        description: formData.description,
        shortDescription: formData.shortDescription,
        gameData: formData.aiGeneratedContent,
        comment: comment || ''
      };

      const headers = {
        'Content-Type': 'application/json',
      };

      // Add reCAPTCHA token if available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/app/regenerate-element`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to regenerate content');
      }

      setFormData(prevData => {
        const updatedData = { ...prevData };
        if (index !== undefined) {
          const newData = [...updatedData.aiGeneratedContent.options.data];
          newData[index] = data.generatedContent;
          updatedData.aiGeneratedContent = {
            ...updatedData.aiGeneratedContent,
            options: {
              ...updatedData.aiGeneratedContent.options,
              data: newData
            }
          };
        } else {
          updatedData.aiGeneratedContent = {
            ...updatedData.aiGeneratedContent,
            options: {
              ...updatedData.aiGeneratedContent.options,
              [elementType]: data.generatedContent
            }
          };
        }
        return updatedData;
      });
    } catch (error) {
      console.error('Error regenerating content:', error);
    } finally {
      setIsRegenerating(prev => ({ ...prev, [regenerationKey]: false }));
    }
  }, [formData.gameType, formData.description, formData.shortDescription, formData.aiGeneratedContent]);

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const { gameType } = formData;
      let gameOptions;

      // Get game type color
      const gameTypeColor = getGameTypeColor(gameType.id);

      // Use the combined style presets from QuickStyleSelector or fall back to getStylePresets
      let stylePresets = allStylePresets;
      if (Object.keys(stylePresets).length === 0) {
        try {
          stylePresets = getStylePresets(gameTypeColor, propBusinessProfile, gameType.id);
        } catch (styleError) {
          console.error('Error getting style presets:', styleError);
          stylePresets = { default: {} };
        }
      }

      // Use the selected style or fall back to default
      console.log('Available style presets:', Object.keys(stylePresets));
      console.log('Selected style:', selectedStyle);
      console.log('Found theme for selected style:', !!stylePresets[selectedStyle]);
      
      let theme = stylePresets[selectedStyle] || stylePresets.default;

      // Initialize base theme colors if they don't exist
      theme = {
        ...theme,
        colors: {
          ...theme.colors,
          titleText: theme.colors?.titleText || '#1F2937',
          subtitleText: theme.colors?.subtitleText || '#4B5563',
          cardBackground: theme.colors?.cardBackground || '#FFFFFF',
          cardOpacity: theme.colors?.cardOpacity || 0.9,
          buttonBackground: theme.colors?.buttonBackground || '#4F46E5',
          buttonText: theme.colors?.buttonText || '#FFFFFF',
          results: {
            ...theme.colors?.results,
            cardBackground: theme.colors?.results?.cardBackground || '#FFFFFF',
            cardBackgroundOpacity: theme.colors?.results?.cardBackgroundOpacity || 0.9,
            scoreLabelText: theme.colors?.results?.scoreLabelText || '#4B5563',
            scoreValueText: theme.colors?.results?.scoreValueText || '#1F2937'
          }
        }
      };

      // For Snake game, ensure game-specific colors are properly initialized
      if (gameType.id === 'snake') {
        theme.colors.game = {
          canvasColor: theme.colors.game?.canvasColor || theme.colors.cardBackground || '#FFFFFF',
          snakeColor: theme.colors.game?.snakeColor || theme.colors.primary || '#00FF00',
          foodColor: theme.colors.game?.foodColor || theme.colors.accent || '#FF0000',
          controlButtonColor: theme.colors.game?.controlButtonColor || theme.colors.cardBackground || '#FFFFFF',
          controlButtonOpacity: theme.colors.game?.controlButtonOpacity ?? theme.colors.cardOpacity ?? 0.9,
          controlIconColor: theme.colors.game?.controlIconColor || theme.colors.primary || '#9CA3AF',
          scoreText: theme.colors.game?.scoreText || theme.colors.titleText || '#4B5563'
        };
      }

      if (gameType.requiresAIGeneration) {
        gameOptions = {
          ...gameType.options,
          ...(formData.aiGeneratedContent?.options || {}),
          theme,
          hideHeadline: true
        };
      } else {
        gameOptions = {
          ...gameType.options,
          theme,
          hideHeadline: true,
          title: formData.shortDescription,
          intro: formData.description
        };
      }

      // Sanitize options before saving to prevent undefined values in Firestore
      const sanitizeGameOptions = (options) => {
        return {
          ...options,
          cta_text: options.cta_text || '',
          cta_link: options.cta_link || ''
        };
      };

      const dataToInsert = {
        gameType: gameType.id,
        gameTypeDisplayValue: gameType.name,
        name: gameOptions.title || formData.shortDescription,
        status: finalConfig.isStudioMode ? 'draft' : 'live',
        arcade: !finalConfig.isStudioMode,
        private: isPrivate === true,
        options: sanitizeGameOptions(deepMergeOptions(gameOptions, {
          ...publicGameOptions,
          creatorName: formData.groupName || null
        })),
        createdAt: new Date(),
        ...finalConfig.additionalData
      };

      const docRef = await addDoc(collection(db, finalConfig.collectionName), dataToInsert);
      
      if (finalConfig.isStudioMode && finalConfig.redirectPath) {
        // Studio mode: redirect to campaigns list
        navigate(`${finalConfig.redirectPath}/${docRef.id}`);
      } else {
        // Public mode: show share screen
        const gameLink = `${window.location.origin}/play/${docRef.id}`;
        setStep(4);
        setFormData(prev => ({ ...prev, shareableLink: gameLink }));
      }
    } catch (error) {
      console.error('Error creating game:', error);
      
      // Provide specific error messages based on error type
      if (error.message?.includes('reCAPTCHA') || error.message?.includes('Invalid reCAPTCHA')) {
        setRateLimitError('🤖 Security check failed. Please refresh the page and try again. If this keeps happening, try using a different browser or disabling ad blockers.');
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        setRateLimitError('🌐 Connection problem! Please check your internet connection and try again.');
      } else if (error.message?.includes('timeout')) {
        setRateLimitError('⏱️ The request took too long. Please try again!');
      } else if (error.message?.includes('500') || error.message?.includes('server')) {
        setRateLimitError('🔧 Our servers are having a moment. Please try again in a few seconds!');
      } else {
        setRateLimitError(`❌ Couldn't create your game: ${error.message || 'Please try again or contact support if this keeps happening.'}`);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const renderDynamicSummary = () => {
    const { aiGeneratedContent } = formData;
    const canRegenerate = formData.gameType?.requiresAIGeneration;
    const title = aiGeneratedContent?.options?.title || manualTitle || formData.shortDescription || 'No title provided';
    const intro = aiGeneratedContent?.options?.intro || manualIntro || formData.description || 'No introduction provided';

    const renderIcon = (IconComponent) => {
      if (!IconComponent) return null;
      const colorClass = formData.gameType?.id === 'pindrop' ? 'text-red-600' :
        formData.gameType?.id === 'hint' ? 'text-amber-600' :
        formData.gameType?.id === 'quiz' ? 'text-blue-600' :
        formData.gameType?.id === 'mapselect' ? 'text-emerald-600' :
        formData.gameType?.id === 'snake' ? 'text-purple-600' :
        'text-indigo-600';
      return <IconComponent className={`w-6 h-6 ${colorClass}`} />;
    };

    return (
      <div className="space-y-2">
        {/* Game Type Summary */}
        <div className="rounded-xl p-6 mb-6 border-2" style={{
          backgroundColor: '#2C3E50',
          borderColor: '#34495E'
        }}>
          <div className="flex items-center space-x-4">
            <div className="p-3 rounded-lg" style={{
              backgroundColor: '#34495E'
            }}>
              {renderIcon(formData.gameType?.icon)}
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ 
                color: '#F1C40F',
                fontFamily: '"Share Tech Mono", monospace',
                textShadow: '1px 1px 0px #B7950B'
              }}>{formData.gameType?.name}</h2>
              <p className="text-sm font-bold" style={{ color: '#ECF0F1' }}>{formData.gameType?.description}</p>
            </div>
          </div>
        </div>

        {/* Read-only Title with regenerate button for AI games */}
        <div className="rounded-lg p-3 border-2" style={{
          backgroundColor: '#34495E',
          borderColor: '#2C3E50'
        }}>
          <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-bold" style={{ 
                          color: '#F1C40F',
                          fontFamily: '"Share Tech Mono", monospace'
                        }}>Title</h3>
            {canRegenerate && (
              <button
                onClick={() => openRegenPopup('title', aiGeneratedContent?.options?.title)}
                className="text-sm transition-all duration-200 hover:scale-105"
                style={{ color: '#F1C40F' }}
                disabled={isRegenerating.title}
              >
                {isRegenerating.title ? (
                  <PulseLoader color="#F1C40F" size={8} />
                ) : (
                  <SparklesIcon className="w-5 h-5 text-yellow-400" />
                )}
              </button>
            )}
          </div>
          <p className="text-sm font-bold mt-1.5" style={{ color: '#ECF0F1' }}>
            {title}
          </p>
        </div>

        {/* Read-only Introduction with regenerate button for AI games */}
        <div className="rounded-lg p-3 border-2" style={{
          backgroundColor: '#34495E',
          borderColor: '#2C3E50'
        }}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold" style={{ 
              color: '#F1C40F',
              fontFamily: '"Share Tech Mono", monospace'
            }}>Introduction</h3>
            {canRegenerate && (
              <button
                onClick={() => openRegenPopup('intro', aiGeneratedContent?.options?.intro)}
                className="text-sm transition-all duration-200 hover:scale-105"
                style={{ color: '#F1C40F' }}
                disabled={isRegenerating.intro}
              >
                {isRegenerating.intro ? (
                  <PulseLoader color="#F1C40F" size={8} />
                ) : (
                  <SparklesIcon className="w-5 h-5 text-yellow-400" />
                )}
              </button>
            )}
          </div>
          <p className="text-sm font-bold mt-1.5" style={{ color: '#ECF0F1' }}>
            {intro}
          </p>
        </div>
      </div>
    );
  };

  // Helper for placeholder
  const getGameTypePlaceholder = () => {
    return formData.gameType?.exampleTopics || 'Enter your game topic';
  };

  // Helper for color
  const gameTypeColor = formData?.gameType?.id ? getGameTypeColor(formData.gameType.id) : "#4F46E5";



  const styles = `
    .slide-enter {
      transform: translateX(100%);
      opacity: 0;
    }
    .slide-enter-active {
      transform: translateX(0%);
      opacity: 1;
      transition: transform 300ms ease-in-out, opacity 300ms ease-in-out;
    }
    .slide-exit {
      transform: translateX(0%);
      opacity: 1;
    }
    .slide-exit-active {
      transform: translateX(-100%);
      opacity: 0;
      transition: transform 300ms ease-in-out, opacity 300ms ease-in-out;
    }

    /* Custom classes for back navigation */
    .slide-back-enter {
      transform: translateX(-100%);
      opacity: 0;
    }
    .slide-back-enter-active {
      transform: translateX(0%);
      opacity: 1;
      transition: transform 300ms ease-in-out, opacity 300ms ease-in-out;
    }
    .slide-back-exit {
      transform: translateX(0%);
      opacity: 1;
    }
    .slide-back-exit-active {
      transform: translateX(100%);
      opacity: 0;
      transition: transform 300ms ease-in-out, opacity 300ms ease-in-out;
    }
  `;

  // Add state for regeneration popup
  const openRegenPopup = (elementType, currentData, index) => {
    setRegenPopup({ open: true, elementType, currentData, index });
    setRegenComment('');
  };
  const closeRegenPopup = () => {
    setRegenPopup({ open: false, elementType: null, currentData: null, index: null });
    setRegenComment('');
  };
  const confirmRegen = () => {
    handleRegenerate(regenPopup.elementType, regenPopup.currentData, regenPopup.index, regenComment);
    closeRegenPopup();
  };

  // In the final (review) page, use the static mapping:
  const SetupComponent = formData.gameType?.id ? setupComponents[formData.gameType.id] : null;

  // Helper for deep merge when updating publicGameOptions
  const deepMergeOptions = (prev, next) => ({
    ...prev,
    ...next,
    theme: {
      ...prev.theme,
      ...(next.theme || {}),
      colors: {
        ...prev.theme.colors,
        ...(next.theme?.colors || {}),
        game: {
          ...prev.theme.colors.game,
          ...(next.theme?.colors?.game || {})
        }
      },
      typography: {
        ...prev.theme.typography,
        ...(next.theme?.typography || {})
      }
    }
  });

  // Mobile-first step indicator component
  const StepIndicator = ({ currentStep, totalSteps }) => {
    const steps = [
      { number: 1, title: "Choose", description: "Pick your game type" },
      { number: 2, title: "Setup", description: "Add your content" },
      { number: 3, title: "Style", description: "Customize appearance" },
      { number: 4, title: "Share", description: "Get your game link" }
    ];

    return (
      <div className="mb-6 px-4">
        {/* Mobile: Simple current step indicator */}
        <div className="sm:hidden">
          <div className="flex items-center justify-center gap-3 py-3 px-4 rounded-lg border-2" style={{
            backgroundColor: '#34495E',
            borderColor: '#2C3E50'
          }}>
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold border-2"
              style={{
                backgroundColor: '#F1C40F',
                borderColor: '#E67E22',
                color: '#2C3E50'
              }}
            >
              {currentStep}
            </div>
            <div>
              <div className="text-sm font-bold" style={{ 
                color: '#F1C40F',
                fontFamily: '"Share Tech Mono", monospace'
              }}>
                Step {currentStep} of {totalSteps}
              </div>
                             <div className="text-xs font-bold" style={{ color: '#ECF0F1' }}>
                 {steps[currentStep - 1]?.description}
               </div>
            </div>
            <div className="flex gap-1 ml-auto">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: index + 1 <= currentStep ? '#E74C3C' : '#2C3E50'
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Desktop: Full step indicator */}
        <div className="hidden sm:flex items-center justify-center max-w-4xl mx-auto">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold border-2 transition-all duration-200"
                  style={
                    step.number === currentStep
                      ? { 
                          backgroundColor: '#F1C40F', 
                          borderColor: '#E67E22', 
                          color: '#2C3E50'
                        }
                      : step.number < currentStep
                      ? { 
                          backgroundColor: '#E74C3C', 
                          borderColor: '#C0392B', 
                          color: '#FFFFFF'
                        }
                      : { 
                          backgroundColor: '#34495E', 
                          borderColor: '#2C3E50', 
                          color: '#ECF0F1'
                        }
                  }
                >
                  {step.number < currentStep ? '✓' : step.number}
                </div>
                                 <div className="mt-1.5 text-center">
                   <div 
                     className="text-xs font-bold"
                     style={{ 
                       color: step.number === currentStep ? '#F1C40F' : '#ECF0F1',
                       fontFamily: '"Share Tech Mono", monospace'
                     }}
                   >
                     {step.title}
                   </div>
                   <div 
                     className="text-xs mt-0.5 hidden md:block whitespace-nowrap"
                     style={{ color: '#BDC3C7' }}
                   >
                     {step.description}
                   </div>
                 </div>
              </div>
              {index < steps.length - 1 && (
                <div 
                  className="w-8 md:w-12 h-0.5 mx-2 md:mx-3 rounded-full"
                  style={{ 
                    backgroundColor: step.number < currentStep ? '#E74C3C' : '#34495E'
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const confettiData = useMemo(() =>
    Array.from({ length: 15 }).map(() => ({
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      backgroundColor: ['#F1C40F', '#E74C3C', '#2ECC71', '#3498DB'][Math.floor(Math.random() * 4)],
      animationDelay: `${Math.random() * 3}s`,
      animationDuration: `${2 + Math.random() * 2}s`
    })), []);

  return (
    <div>
      {/* Hero Section */}
      <header className="relative text-center overflow-hidden flex flex-col items-center justify-center">
      </header>
      
      <div className={finalConfig.isStudioMode ? '' : 'px-4 sm:px-6 lg:px-8 py-4 sm:py-8'}>
        <div className=" mx-auto w-full">
                      <div ref={gameCreationRef} className={`rounded-2xl shadow-lg border-2 ${finalConfig.isStudioMode ? 'p-4 sm:p-6' : 'p-4 sm:p-6 lg:p-8'}`} style={{
            backgroundColor: '#34495E',
            borderColor: '#2C3E50'
          }}>
            {/* Add Step Indicator - Hidden on mobile for step 4 */}
            {!isGenerating && (
              <div className={step === 4 ? "hidden sm:block" : ""}>
                <StepIndicator currentStep={step} totalSteps={4} />
              </div>
            )}
            
            {rateLimitError && (
              <div className="mb-4 p-4 rounded-lg border-2" style={{
                backgroundColor: '#FFF3CD',
                borderColor: '#FFEAA7',
                color: '#856404'
              }}>
                <div className="flex items-start gap-2">
                  <div className="text-lg">⚠️</div>
                  <div className="font-bold text-sm">
                    {rateLimitError}
                  </div>
                </div>
              </div>
            )}
            {isGenerating ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="flex flex-col items-center justify-center min-h-[400px] space-y-6"
              >
                <div className="relative">
                  <motion.div 
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-16 h-16 border-4 rounded-full"
                    style={{
                      borderColor: '#2C3E50',
                      borderTopColor: '#F1C40F'
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <RocketLaunchIcon 
                      className="w-8 h-8"
                      style={{ color: '#E74C3C' }}
                    />
                  </div>
                </div>
                <AnimatePresence initial={true} mode="wait">
                  <motion.div
                    key={loadingMessage}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{
                      duration: 0.5,
                      ease: [0.4, 0.0, 0.2, 1]
                    }}
                    className="text-center"
                  >
                    <p className="text-sm font-bold" style={{ color: '#F1C40F' }}>
                      {loadingMessage}
                    </p>
                  </motion.div>
                </AnimatePresence>
                <motion.div
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-xs font-bold"
                  style={{ color: '#ECF0F1' }}
                >
                  This may take a few moments...
                </motion.div>
              </motion.div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="w-full"
                >
                  {step === 1 && (
                    <>
                      <div className="w-full">
                        <div className="mb-8">
                          <h2 className="hidden sm:block text-3xl font-bold mb-3" style={{ 
                            color: '#F1C40F',
                            fontFamily: '"Share Tech Mono", monospace',
                            textShadow: '2px 2px 0px #B7950B'
                          }}>{finalConfig.stepTitles.step1}</h2>
                          <p className="hidden md:block text-base font-bold" style={{ color: '#ECF0F1' }}>Pick the game that sounds the most fun! Some games have AI-generated content, others are instant arcade classics.</p>
                        </div>

                        {/* Most Popular Section */}
                        <div className="mb-8">
                          <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 rounded-lg" style={{ backgroundColor: '#E74C3C' }}>
                              <StarIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold" style={{ 
                                color: '#F1C40F',
                                fontFamily: '"Share Tech Mono", monospace',
                                textShadow: '1px 1px 0px #B7950B'
                              }}>Most Popular</h3>
                              <p className="text-sm font-bold" style={{ color: '#ECF0F1' }}>
                                Our community favorites - tried and tested!
                              </p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                            {gameTypes
                              .filter(type => ['listmaster', 'snake', 'cubefield'].includes(type.id))
                              .sort((a, b) => {
                                const order = ['listmaster', 'snake', 'cubefield'];
                                return order.indexOf(a.id) - order.indexOf(b.id);
                              })
                              .map((type) => (
                              <div
                                className="group w-full rounded-xl shadow-lg hover:shadow-2xl transition-all duration-200 border-2 relative overflow-hidden cursor-pointer"
                                onClick={() => handleGameTypeSelect(type)}
                                style={{
                                  backgroundColor: '#34495E',
                                  borderColor: '#2C3E50',
                                  minHeight: '220px'
                                }}
                              >

                                
                                <div className="p-4 pb-20 h-full flex flex-col">
                                  <div className="flex items-start gap-4 mb-4">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-xl flex-shrink-0 shadow-sm" style={{
                                      backgroundColor: '#2C3E50'
                                    }}>
                                      {type.icon && <type.icon 
                                        className="w-6 h-6"
                                        style={{ color: '#F1C40F' }}
                                      />}
                                    </div>
                                    <div className="flex-1 min-w-0 pr-12">
                                      <h3 className="text-xl font-bold mb-2 leading-tight" style={{ 
                                        color: '#F1C40F',
                                        fontFamily: '"Share Tech Mono", monospace',
                                        textShadow: '1px 1px 0px #B7950B'
                                      }}>
                                        {type.name}
                                      </h3>
                                      {type.exampleTopics ? (
                                        <p className="text-sm font-bold opacity-75 leading-relaxed" style={{ color: '#ECF0F1' }}>
                                          {type.exampleTopics}
                                        </p>
                                      ) : (
                                        <p className="text-sm font-bold opacity-75 leading-relaxed" style={{ color: '#ECF0F1' }}>
                                          Classic arcade game
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <p className="text-sm font-bold flex-1 mb-4 leading-relaxed" style={{ color: '#ECF0F1' }}>
                                    {type.description}
                                  </p>
                                </div>
                                
                                {/* SELECT Button - Positioned at bottom */}
                                <div className="absolute bottom-0 left-0 right-0 p-4">
                                  <div className="w-full rounded-xl py-3 px-6 text-center font-bold text-white transition-all duration-200 group-hover:opacity-90 border-2" style={{
                                    fontFamily: '"Share Tech Mono", monospace',
                                    backgroundColor: '#E74C3C',
                                    borderColor: '#F1C40F',
                                    textShadow: '1px 1px 0px #A93226'
                                  }}>
                                    SELECT
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Other Games Section */}
                        <div>
                          <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 rounded-lg" style={{ backgroundColor: '#3498DB' }}>
                              <SparklesIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold" style={{ 
                                color: '#F1C40F',
                                fontFamily: '"Share Tech Mono", monospace',
                                textShadow: '1px 1px 0px #B7950B'
                              }}>More Games</h3>
                              <p className="text-sm font-bold" style={{ color: '#ECF0F1' }}>
                                Explore more gaming experiences
                              </p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {gameTypes
                              .filter(type => !['listmaster', 'snake', 'cubefield'].includes(type.id))
                              .map((type) => (
                              <div
                                className="group w-full rounded-xl shadow-lg hover:shadow-2xl transition-all duration-200 border-2 relative overflow-hidden cursor-pointer"
                                onClick={() => handleGameTypeSelect(type)}
                                style={{
                                  backgroundColor: '#34495E',
                                  borderColor: '#2C3E50',
                                  minHeight: '220px'
                                }}
                              >
                                <div className="p-4 pb-20 h-full flex flex-col">
                                  <div className="flex items-start gap-4 mb-4">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-xl flex-shrink-0 shadow-sm" style={{
                                      backgroundColor: '#2C3E50'
                                    }}>
                                      {type.icon && <type.icon 
                                        className="w-6 h-6"
                                        style={{ color: '#F1C40F' }}
                                      />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h3 className="text-xl font-bold mb-2 leading-tight" style={{ 
                                        color: '#F1C40F',
                                        fontFamily: '"Share Tech Mono", monospace',
                                        textShadow: '1px 1px 0px #B7950B'
                                      }}>
                                        {type.name}
                                      </h3>
                                      {type.exampleTopics ? (
                                        <p className="text-sm font-bold opacity-75 leading-relaxed" style={{ color: '#ECF0F1' }}>
                                          {type.exampleTopics}
                                        </p>
                                      ) : (
                                        <p className="text-sm font-bold opacity-75 leading-relaxed" style={{ color: '#ECF0F1' }}>
                                          Classic arcade game
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <p className="text-sm font-bold flex-1 mb-4 leading-relaxed" style={{ color: '#ECF0F1' }}>
                                    {type.description}
                                  </p>
                                </div>
                                
                                {/* SELECT Button - Positioned at bottom */}
                                <div className="absolute bottom-0 left-0 right-0 p-4">
                                  <div className="w-full rounded-xl py-3 px-6 text-center font-bold text-white transition-all duration-200 group-hover:opacity-90 border-2" style={{
                                    fontFamily: '"Share Tech Mono", monospace',
                                    backgroundColor: '#E74C3C',
                                    borderColor: '#F1C40F',
                                    textShadow: '1px 1px 0px #A93226'
                                  }}>
                                    SELECT
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {step === 2 && formData.gameType && (
                    formData.gameType.requiresAIGeneration ? (
                      // AI-required setup page
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="max-w-4xl mx-auto"
                      >
                        <div className="mb-8">
                          <h2 className="text-3xl font-bold" style={{ 
                            color: '#F1C40F',
                            fontFamily: '"Share Tech Mono", monospace',
                            textShadow: '2px 2px 0px #B7950B'
                          }}>{finalConfig.stepTitles.step2}</h2>
                          <p className="mt-2 text-base font-bold" style={{ color: '#ECF0F1' }}>Tell us what you want to play and we'll create it!</p>
                        </div>
                        <form onSubmit={handleBasicInfoSubmit} className="mt-4">
                          <fieldset className="mb-6">
                            <legend className="text-base font-bold leading-6 mb-3" style={{ 
                              color: '#F1C40F',
                              fontFamily: '"Share Tech Mono", monospace'
                            }}>What's your game about?</legend>
                            <div className="mt-2">
                              <textarea
                                value={formData.shortDescription || ''}
                                onChange={e => setFormData(prev => ({ ...prev, shortDescription: e.target.value }))}
                                rows={1}
                                className="block w-full rounded-lg shadow-sm focus:ring-2 focus:ring-offset-2 text-base resize-none leading-tight transition-all duration-200 border-2 p-4"
                                style={{ 
                                  borderColor: '#2C3E50',
                                  backgroundColor: '#2C3E50',
                                  color: '#ECF0F1',
                                  outlineColor: '#F1C40F'
                                }}
                                placeholder={getGameTypePlaceholder()}
                                required
                              />
                            </div>
                          </fieldset>
                          
                          {!finalConfig.isStudioMode && (
                            <fieldset className="mb-6">
                              <legend className="text-base font-bold leading-6 mb-3" style={{ 
                                color: '#F1C40F',
                                fontFamily: '"Share Tech Mono", monospace'
                              }}>Created for (optional)</legend>
                              <div className="mt-2">
                                <input
                                  type="text"
                                  value={formData.groupName || ''}
                                  onChange={e => {
                                    const value = e.target.value;
                                    if (value.length <= MAX_GROUP_NAME_LENGTH) {
                                      setFormData(prev => ({ ...prev, groupName: value }));
                                    }
                                  }}
                                  maxLength={MAX_GROUP_NAME_LENGTH}
                                  className="block w-full rounded-lg shadow-sm focus:ring-2 focus:ring-offset-2 text-base transition-all duration-200 border-2 p-4"
                                  style={{ 
                                    borderColor: '#2C3E50',
                                    backgroundColor: '#2C3E50',
                                    color: '#ECF0F1',
                                    outlineColor: '#F1C40F'
                                  }}
                                  placeholder="e.g., Sarah's Brunch Club, my coworkers, the book club"
                                />
                              </div>
                              <div className="mt-2 flex justify-between items-start">
                                <p className="text-sm font-bold" style={{ color: '#95A5A6' }}>
                                  This will show up as "Created for {formData.groupName || 'your group'}" on your game
                                </p>
                                <span className="text-xs font-bold ml-2 flex-shrink-0" style={{ 
                                  color: formData.groupName?.length > MAX_GROUP_NAME_LENGTH * 0.8 ? '#E74C3C' : '#95A5A6' 
                                }}>
                                  {formData.groupName?.length || 0}/{MAX_GROUP_NAME_LENGTH}
                                </span>
                              </div>
                            </fieldset>
                          )}
                          <div className="mt-8 flex justify-between">
                            <button
                              type="button"
                              onClick={() => { setIsBackward(true); setStep(1); }}
                              className="px-6 py-3 rounded-lg font-bold border-2 transition-all duration-200 hover:scale-105 text-base"
                              style={{
                                backgroundColor: '#34495E',
                                color: '#ECF0F1',
                                borderColor: '#2C3E50',
                                fontFamily: '"Share Tech Mono", monospace'
                              }}
                            >
                              BACK
                            </button>
                            <button
                              type="submit"
                              className="px-8 py-3 text-base font-bold text-white border-2 rounded-lg shadow-lg transition-all duration-200 hover:scale-105"
                              style={{ 
                                background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                                borderColor: '#F1C40F',
                                boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)',
                                fontFamily: '"Share Tech Mono", monospace',
                                textShadow: '1px 1px 0px #A93226'
                              }}
                            >
                              GENERATE GAME →
                            </button>
                          </div>
                        </form>
                      </motion.div>
                    ) : (
                      // Non-AI setup page
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="max-w-4xl mx-auto"
                      >
                        <div className="mb-8">
                          <h2 className="text-3xl font-bold" style={{ 
                            color: '#F1C40F',
                            fontFamily: '"Share Tech Mono", monospace',
                            textShadow: '2px 2px 0px #B7950B'
                          }}>Name your game</h2>
                          <p className="mt-2 text-base font-bold" style={{ color: '#ECF0F1' }}>Give it a title and description!</p>
                        </div>
                        <form onSubmit={e => {
                          e.preventDefault();
                          setFormData(prev => ({
                            ...prev,
                            shortDescription: manualTitle,
                            description: manualIntro,
                            aiGeneratedContent: {
                              options: {
                                ...((prev.aiGeneratedContent && prev.aiGeneratedContent.options) || {}),
                                title: manualTitle,
                                intro: manualIntro
                              }
                            }
                          }));
                          setStep(3);
                        }} className="mt-4 space-y-6">
                          <div>
                            <label className="block text-base font-bold mb-3" style={{ 
                              color: '#F1C40F',
                              fontFamily: '"Share Tech Mono", monospace'
                            }}>Title</label>
                            <input
                              type="text"
                              className="mt-1 block w-full rounded-lg shadow-sm focus:ring-2 focus:ring-offset-2 text-base border-2 transition-all duration-200 p-4"
                              style={{ 
                                borderColor: '#2C3E50',
                                backgroundColor: '#2C3E50',
                                color: '#ECF0F1',
                                outlineColor: '#F1C40F'
                              }}
                              placeholder="Your game title"
                              value={manualTitle}
                              onChange={e => setManualTitle(e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-base font-bold mb-3" style={{ 
                              color: '#F1C40F',
                              fontFamily: '"Share Tech Mono", monospace'
                            }}>Introduction</label>
                            <textarea
                              className="mt-1 block w-full rounded-lg shadow-sm focus:ring-2 focus:ring-offset-2 text-base border-2 resize-none transition-all duration-200 p-4"
                              style={{ 
                                borderColor: '#2C3E50',
                                backgroundColor: '#2C3E50',
                                color: '#ECF0F1',
                                outlineColor: '#F1C40F'
                              }}
                              placeholder="What should players expect?"
                              value={manualIntro}
                              onChange={e => setManualIntro(e.target.value)}
                              rows={2}
                            />
                          </div>
                          
                          {!finalConfig.isStudioMode && (
                            <div>
                              <label className="block text-base font-bold mb-3" style={{ 
                                color: '#F1C40F',
                                fontFamily: '"Share Tech Mono", monospace'
                              }}>Created for (optional)</label>
                              <input
                                type="text"
                                className="mt-1 block w-full rounded-lg shadow-sm focus:ring-2 focus:ring-offset-2 text-base border-2 transition-all duration-200 p-4"
                                style={{ 
                                  borderColor: '#2C3E50',
                                  backgroundColor: '#2C3E50',
                                  color: '#ECF0F1',
                                  outlineColor: '#F1C40F'
                                }}
                                placeholder="e.g., Sarah's Brunch Club, my coworkers, the book club"
                                value={formData.groupName}
                                onChange={e => {
                                  const value = e.target.value;
                                  if (value.length <= MAX_GROUP_NAME_LENGTH) {
                                    setFormData(prev => ({ ...prev, groupName: value }));
                                  }
                                }}
                                maxLength={MAX_GROUP_NAME_LENGTH}
                              />
                              <div className="mt-2 flex justify-between items-start">
                                <p className="text-sm font-bold" style={{ color: '#95A5A6' }}>
                                  This will show up as "Created for {formData.groupName || 'your group'}" on your game
                                </p>
                                <span className="text-xs font-bold ml-2 flex-shrink-0" style={{ 
                                  color: formData.groupName?.length > MAX_GROUP_NAME_LENGTH * 0.8 ? '#E74C3C' : '#95A5A6' 
                                }}>
                                  {formData.groupName?.length || 0}/{MAX_GROUP_NAME_LENGTH}
                                </span>
                              </div>
                            </div>
                          )}
                          <div className="mt-8 flex justify-between">
                            <button
                              type="button"
                              onClick={() => { setIsBackward(true); setStep(1); }}
                              className="px-6 py-3 rounded-lg font-bold border-2 transition-all duration-200 hover:scale-105 text-base"
                              style={{
                                backgroundColor: '#34495E',
                                color: '#ECF0F1',
                                borderColor: '#2C3E50',
                                fontFamily: '"Share Tech Mono", monospace'
                              }}
                            >
                              BACK
                            </button>
                            <button
                              type="submit"
                              className="px-8 py-3 text-base font-bold text-white rounded-lg shadow-lg transition-all duration-200 hover:scale-105"
                              style={{ 
                                background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                                borderColor: '#F1C40F',
                                boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)',
                                fontFamily: '"Share Tech Mono", monospace',
                                textShadow: '1px 1px 0px #A93226'
                              }}
                            >
                              CONTINUE →
                            </button>
                          </div>
                        </form>
                      </motion.div>
                    )
                  )}

                  {step === 3 && (
                    <div className="space-y-6">
                      <div className="mb-8">
                        <h2 className="text-3xl font-bold" style={{ 
                          color: '#F1C40F',
                          fontFamily: '"Share Tech Mono", monospace',
                          textShadow: '2px 2px 0px #B7950B'
                        }}>
                          {finalConfig.stepTitles.step3}
                        </h2>
                        <p className="mt-2 text-base font-bold" style={{ color: '#ECF0F1' }}>
                          Make any final tweaks before {finalConfig.isStudioMode ? 'creating your campaign' : 'sharing'}
                        </p>
                      </div>

                      {renderDynamicSummary()}

                      {/* Style Selector - styled to match title/intro sections */}
                      <div className="rounded-lg p-3 border-2" style={{
                        backgroundColor: '#34495E',
                        borderColor: '#2C3E50'
                      }}>
                        <h3 className="text-sm font-bold" style={{ 
                          color: '#F1C40F',
                          fontFamily: '"Share Tech Mono", monospace'
                        }}>Choose Style</h3>
                        <p className="text-sm font-bold mt-1.5 mb-4" style={{ color: '#ECF0F1' }}>
                          Pick a style for your game
                        </p>
                        <QuickStyleSelector
                          selectedStyle={selectedStyle}
                          onStyleSelect={setSelectedStyle}
                          businessProfile={propBusinessProfile}
                          formData={formData}
                          title={formData.aiGeneratedContent?.options?.title || manualTitle || formData.shortDescription}
                          description={formData.aiGeneratedContent?.options?.intro || manualIntro || formData.description}
                          helpText=""
                          isPublicGame={!finalConfig.isStudioMode}
                          onStylePresetsUpdate={setAllStylePresets}
                        />
                      </div>

                      {/* Appearance customization options - styled to match title/intro sections */}
                      {SetupComponent && (
                        <div className="rounded-lg p-3 border-2" style={{
                          borderColor: '#2C3E50'
                        }}>
                          <h3 className="text-sm font-bold mb-4" style={{ 
                            color: '#F1C40F',
                            fontFamily: '"Share Tech Mono", monospace'
                          }}>Game Icons</h3>
                          <Suspense fallback={<div className="p-4 rounded-lg border-2" style={{
                            backgroundColor: '#2C3E50',
                            borderColor: '#34495E',
                            color: '#ECF0F1'
                          }}>Loading customization...</div>}>
                            <SetupComponent
                              safeOptions={publicGameOptions || { theme: { colors: { game: {} }, typography: {} } }}
                              onOptionsUpdate={update => setPublicGameOptions(prev => deepMergeOptions(prev, update))}
                              activeColorPicker={activeColorPicker}
                              setActiveColorPicker={setActiveColorPicker}
                              section="surface"
                            />
                          </Suspense>
                        </div>
                      )}

                      {/* Privacy Settings - only show if not studio mode or if explicitly enabled */}
                      {finalConfig.showPrivacyToggle && (
                        <div className="rounded-lg border-2 p-4" style={{
                          backgroundColor: '#34495E',
                          borderColor: '#2C3E50'
                        }}>
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg" style={{ backgroundColor: '#2C3E50' }}>
                              <LockClosedIcon className="w-5 h-5" style={{ color: '#F1C40F' }} />
                            </div>
                            <div className="flex-1">
                              <FormSwitch
                                name="private"
                                label="Private"
                                subtitle="Only people with the link will be able to access this."
                                checked={isPrivate}
                                onChange={(enabled) => setIsPrivate(enabled)}
                                labelStyle={{ color: '#F1C40F' }}
                                subtitleStyle={{ color: '#ECF0F1' }}
                                switchStyle={{
                                  enabledColor: '#E74C3C',
                                  disabledColor: '#2C3E50'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between pt-6">
                        <button
                          onClick={() => {
                            setIsBackward(true);
                            setStep(2);
                          }}
                          className="px-6 py-3 rounded-lg font-bold border-2 transition-all duration-200 hover:scale-105 text-base"
                          style={{
                            backgroundColor: '#34495E',
                            color: '#ECF0F1',
                            borderColor: '#2C3E50',
                            fontFamily: '"Share Tech Mono", monospace'
                          }}
                        >
                          BACK
                        </button>
                        <button
                          onClick={handleCreate}
                          disabled={isCreating}
                          className="px-8 py-3 font-bold text-white rounded-lg border-2 transition-all duration-200 hover:scale-105 shadow-lg text-base"
                          style={{
                            background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                            borderColor: '#F1C40F',
                            boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)',
                            fontFamily: '"Share Tech Mono", monospace',
                            textShadow: '1px 1px 0px #A93226',
                            opacity: isCreating ? 0.7 : 1,
                            cursor: isCreating ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {isCreating ? (
                            <span className="flex items-center gap-2 justify-center">
                              <PulseLoader color="#fff" size={8} />
                              {finalConfig.isStudioMode ? 'CREATING...' : 'CREATING...'}
                            </span>
                          ) : (
                            finalConfig.buttonText.create.toUpperCase()
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {step === 4 && !finalConfig.isStudioMode && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="max-w-4xl mx-auto relative"
                    >
                      {/* Subtle Confetti Animation */}
                      <div className="absolute inset-0 pointer-events-none overflow-hidden">
                        {confettiData.map((dot, i) => (
                          <div
                            key={i}
                            className="absolute w-1.5 h-1.5 rounded-full animate-pulse"
                            style={dot}
                          />
                        ))}
                      </div>

                      <div className="mb-8 text-center relative z-10">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full shadow-lg mb-4 border-2" style={{
                          backgroundColor: '#2ECC71',
                          borderColor: '#27AE60'
                        }}>
                          <CheckIcon className="h-8 w-8 text-white" />
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ 
                          color: '#F1C40F',
                          fontFamily: '"Share Tech Mono", monospace',
                          textShadow: '2px 2px 0px #B7950B'
                        }}>
                          {finalConfig.buttonText.success}
                        </h2>
                        <p className="text-base sm:text-lg font-bold" style={{ color: '#ECF0F1' }}>
                          Play and share with friends
                        </p>
                      </div>

                      {/* Hero Action - Play Game */}
                      <div className="mb-8 text-center relative z-10">
                        <button
                          onClick={() => {
                            window.open(formData.shareableLink, '_blank', 'noopener,noreferrer');
                          }}
                          className="w-full sm:w-auto px-10 sm:px-12 py-5 text-white rounded-xl font-bold text-lg sm:text-xl shadow-lg transition-all duration-200 hover:scale-105 hover:shadow-xl border-2 inline-flex items-center justify-center gap-3"
                          style={{
                            background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                            borderColor: '#F1C40F',
                            boxShadow: '0 8px 20px rgba(231, 76, 60, 0.4)',
                            fontFamily: '"Share Tech Mono", monospace',
                            textShadow: '2px 2px 0px #A93226'
                          }}
                        >
                          <PlayIcon className="w-6 h-6" />
                          PLAY YOUR GAME
                        </button>
                      </div>

                      {isPrivate && (
                        <div className="mb-6 p-4 rounded-lg border-2 flex items-center gap-3 relative z-10" style={{
                          backgroundColor: '#34495E',
                          borderColor: '#F1C40F'
                        }}>
                          <LockClosedIcon className="w-5 h-5 flex-shrink-0" style={{ color: '#F1C40F' }} />
                          <span className="text-sm sm:text-base font-bold" style={{ color: '#F1C40F' }}>
                            Private Game • Only people with the link can access
                          </span>
                        </div>
                      )}

                      <div className="relative z-10">
                        {/* Mobile-First Actions */}
                        <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-4">
                          {/* Copy Link - Mobile: Show as button, Desktop: Show URL */}
                          <div className="flex-1">
                            <div className="sm:hidden">
                              {/* Mobile: Just a copy button */}
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(formData.shareableLink);
                                  setCopySuccess(true);
                                  setTimeout(() => setCopySuccess(false), 2000);
                                }}
                                className="w-full p-4 text-white rounded-lg font-bold transition-all duration-150 hover:scale-105 border-2 flex items-center justify-center gap-3"
                                style={{
                                  background: 'linear-gradient(135deg, #3498DB, #2980B9)',
                                  borderColor: '#F1C40F',
                                  fontFamily: '"Share Tech Mono", monospace',
                                  textShadow: '1px 1px 0px #2471A3'
                                }}
                              >
                                {copySuccess ? '✅ COPIED!' : 'COPY GAME LINK'}
                              </button>
                            </div>
                            <div className="hidden sm:flex gap-3 items-center p-4 rounded-lg border-2" style={{
                              backgroundColor: '#34495E',
                              borderColor: '#2C3E50'
                            }}>
                              {/* Desktop: Show full URL */}
                              <p className="text-sm font-bold font-mono truncate flex-1 min-w-0" style={{ 
                                color: '#ECF0F1'
                              }}>
                                {formData.shareableLink}
                              </p>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(formData.shareableLink);
                                  setCopySuccess(true);
                                  setTimeout(() => setCopySuccess(false), 2000);
                                }}
                                className="px-5 py-3 text-white rounded-lg font-bold transition-all duration-150 hover:scale-105 border-2 flex items-center justify-center gap-2"
                                style={{
                                  background: 'linear-gradient(135deg, #3498DB, #2980B9)',
                                  borderColor: '#F1C40F',
                                  fontFamily: '"Share Tech Mono", monospace',
                                  textShadow: '1px 1px 0px #2471A3'
                                }}
                              >
                                {copySuccess ? '✅ COPIED!' : 'COPY'}
                              </button>
                              {/* WhatsApp Share - Desktop */}
                              <button
                                onClick={() => {
                                  const text = `Check out this game: ${formData.shareableLink}`;
                                  const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
                                  window.open(url, '_blank', 'noopener,noreferrer');
                                }}
                                className="px-5 py-3 rounded-lg font-bold transition-all duration-150 hover:scale-105 border-2 flex items-center justify-center gap-2"
                                style={{
                                  background: 'linear-gradient(135deg, #25D366, #20BA5A)',
                                  borderColor: '#F1C40F',
                                  color: '#FFFFFF',
                                  fontFamily: '"Share Tech Mono", monospace',
                                  textShadow: '1px 1px 0px #1DA851'
                                }}
                              >
                                💬 SHARE ON WHATSAPP
                              </button>
                            </div>
                          </div>
                          
                          {/* WhatsApp Share - Mobile only */}
                          <div className="sm:hidden">
                            <button
                              onClick={() => {
                                const text = `Check out this game: ${formData.shareableLink}`;
                                const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                              className="w-full p-4 rounded-lg font-bold transition-all duration-150 hover:scale-105 border-2 flex items-center justify-center gap-2"
                              style={{
                                background: 'linear-gradient(135deg, #25D366, #20BA5A)',
                                borderColor: '#F1C40F',
                                color: '#FFFFFF',
                                fontFamily: '"Share Tech Mono", monospace',
                                textShadow: '1px 1px 0px #1DA851'
                              }}
                            >
                              💬 SHARE ON WHATSAPP
                            </button>
                          </div>
                        </div>

                        {/* Business Conversion Banner */}
                        <div className="mt-8 p-4 rounded-lg border relative z-10" style={{
                          backgroundColor: '#34495E',
                          borderColor: '#2C3E50'
                        }}>
                          <div className="text-center">
                            <div className="text-sm font-bold mb-2" style={{ 
                              color: '#F1C40F',
                              fontFamily: '"Share Tech Mono", monospace'
                            }}>
                              💡 Want to customize your game further?
                            </div>
                            <p className="text-xs sm:text-sm mb-3" style={{ color: '#ECF0F1' }}>
                              Add your branding, collect player info, and track performance
                            </p>
                            <button
                              onClick={() => window.open('/studio/register', '_blank')}
                              className="px-4 py-2 font-bold rounded-lg border transition-all duration-200 hover:opacity-80 text-xs sm:text-sm"
                              style={{
                                backgroundColor: '#3498DB',
                                borderColor: '#2980B9',
                                color: '#FFFFFF',
                                fontFamily: '"Share Tech Mono", monospace'
                              }}
                            >
                              Learn More
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>
      

      {regenPopup.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Regenerate with instructions</h3>
            <p className="text-sm text-gray-500 mb-2">Optionally add a comment or instruction for the AI to improve this content.</p>
            <textarea
              className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white mb-4"
              rows={3}
              placeholder="E.g. Make it more playful, add a fun fact, etc."
              value={regenComment}
              onChange={e => setRegenComment(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={closeRegenPopup}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmRegen}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(-10px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-10px); }
        }
        .animate-fade-in-out {
          animation: fadeInOut 1.5s both;
        }
      `}</style>
    </div>
  );
};

export default PublicGameCreator; 